import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import ArticleCard from "@/components/ArticleCard";
import { getArticleBySlug, getRelatedArticles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const slug = id || "";

  const article = useMemo(() => getArticleBySlug(slug), [slug]);
  const relatedArticles = useMemo(
    () =>
      article
        ? getRelatedArticles(article.id, article.categorySlug, 4)
        : [],
    [article]
  );

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到"
          description="该文章不存在或已被移除，返回51吃瓜网首页浏览更多吃瓜资讯。"
          canonicalUrl="http://yuefenggroup.cn/"
        />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground-950 mb-4">
              文章未找到
            </h1>
            <p className="text-foreground-600 mb-6">
              该文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://yuefenggroup.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: article.categoryName,
        item: `http://yuefenggroup.cn/category/${article.categorySlug}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
        item: `http://yuefenggroup.cn/article/${article.slug}`,
      },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    image: article.imageUrl,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "51吃瓜网",
      logo: {
        "@type": "ImageObject",
        url: "http://yuefenggroup.cn/logo.png",
      },
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `http://yuefenggroup.cn/article/${article.slug}`,
    },
    articleSection: article.categoryName,
    keywords: article.tags.join(","),
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: article.title,
    url: `http://yuefenggroup.cn/article/${article.slug}`,
    description: article.summary,
    isPartOf: {
      "@type": "WebSite",
      name: "51吃瓜网",
      url: "http://yuefenggroup.cn/",
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={article.tags.join(",")}
        canonicalUrl={`http://yuefenggroup.cn/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.summary}
        ogImage={article.imageUrl}
        ogType="article"
        lastModified={article.publishDate}
        schemaData={[breadcrumbSchema, articleSchema, webPageSchema]}
      />

      <Navbar />

      <main>
        {/* Article Header Image */}
        <section className="relative h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 via-background-950/20 to-transparent" />
        </section>

        {/* Article Content */}
        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumbs
                items={[
                  {
                    label: article.categoryName,
                    path: `/category/${article.categorySlug}`,
                  },
                  { label: article.title },
                ]}
              />
            </div>

            <article>
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full"
                  >
                    {article.categoryName}
                  </Link>
                  {article.isHot && (
                    <span className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-full flex items-center gap-1">
                      <i className="ri-fire-line" /> 热门
                    </span>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line" />
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {article.readCount.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-accent-500" />
                    热度 {article.hotScore}
                  </span>
                </div>
              </header>

              <div className="flex gap-2 mb-8 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                style={{
                  fontFamily: "var(--font-body)",
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: article.content }}
                  className="space-y-4"
                />
              </div>

              {/* Share section */}
              <div className="mt-10 pt-8 border-t border-background-200">
                <p className="text-sm text-foreground-500 mb-3">
                  分享这篇文章：
                </p>
                <div className="flex gap-3">
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-primary-500 hover:text-background-50 transition-colors"
                    aria-label="分享到站内动态"
                  >
                    <i className="ri-chat-1-line" />
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-primary-500 hover:text-background-50 transition-colors"
                    aria-label="分享到微信"
                  >
                    <i className="ri-wechat-line" />
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 hover:bg-primary-500 hover:text-background-50 transition-colors"
                    aria-label="复制链接"
                  >
                    <i className="ri-link" />
                  </button>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                相关推荐
              </h2>
              <p className="text-foreground-600 text-sm md:text-base mb-6">
                同分类下的其他热门内容
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}
